import { useMutation } from '@tanstack/react-query';
import { App, Form, Modal, Select } from 'antd';
import type { ReportKind } from '@titularis/api-client';
import { api } from '../api';
import { registrarEvento } from '../analytics';

/**
 * Reporte de erro.
 *
 * O aluno só escolhe o tipo do erro numa lista curta - sem texto livre. Nas
 * discursivas não há gabarito nem itens, então algumas opções não se aplicam.
 */
const TIPOS: { value: ReportKind; label: string }[] = [
  { value: 'statement', label: 'Erro no enunciado ou nos itens' },
  { value: 'answer_key', label: 'Gabarito errado' },
  { value: 'annulled', label: 'Questão anulada' },
  { value: 'outdated', label: 'Questão desatualizada' },
  { value: 'duplicated', label: 'Questão duplicada' },
];

/** Discursiva não tem gabarito nem itens: sobram enunciado e desatualização. */
const TIPOS_DISCURSIVA: ReportKind[] = ['statement', 'outdated'];

/**
 * A anulada não tem gabarito, e já é anulada: "gabarito errado" e "questão
 * anulada" seriam reportar o que a própria tela já diz (D-61).
 */
const TIPOS_ANULADA: ReportKind[] = ['statement', 'outdated', 'duplicated'];

interface Formulario {
  kind: ReportKind;
}

export function ReportarErro({
  questionId,
  discursiva,
  anulada = false,
  aberto,
  onClose,
}: {
  questionId: string;
  discursiva: boolean;
  anulada?: boolean;
  aberto: boolean;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm<Formulario>();

  const enviar = useMutation({
    mutationFn: (valores: Formulario) => api.createReport({ questionId, kind: valores.kind }),
    onSuccess: (_, valores) => {
      message.success('Obrigado. Vamos revisar esta questão.');
      registrarEvento({ nome: 'error_reported', questionId, kind: valores.kind });
      form.resetFields();
      onClose();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const opcoes = discursiva
    ? TIPOS.filter((item) => TIPOS_DISCURSIVA.includes(item.value))
    : anulada
      ? TIPOS.filter((item) => TIPOS_ANULADA.includes(item.value))
      : TIPOS;

  return (
    <Modal
      open={aberto}
      title="Reportar um erro"
      okText="Enviar"
      cancelText="Cancelar"
      confirmLoading={enviar.isPending}
      onCancel={onClose}
      onOk={() => void form.submit()}
    >
      <Form<Formulario> form={form} layout="vertical" onFinish={(valores) => enviar.mutate(valores)}>
        <Form.Item
          name="kind"
          label="O que está errado?"
          rules={[{ required: true, message: 'Escolha o tipo do erro.' }]}
        >
          <Select placeholder="Escolha" options={opcoes} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
